import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AppLayout, Gate } from './components/layout/AppLayout';
import type { Section } from './components/layout/nav';
import { landingFor } from './components/layout/workspaces';
import { Toasts } from './components/layout/Toasts';
import { Loading } from './components/ui';
import { useAuth } from './stores/auth';
import { useWorkspace } from './stores/workspace';

const Login = lazy(() => import('./pages/Login'));
const Reports = lazy(() => import('./pages/Reports'));
const Today = lazy(() => import('./pages/Today'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Leads = lazy(() => import('./pages/Leads'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Cases = lazy(() => import('./pages/Cases'));
const CaseDetail = lazy(() => import('./pages/CaseDetail'));
const Catalog = lazy(() => import('./pages/Catalog'));
const ProductCard = lazy(() => import('./pages/ProductCard'));
const Dictionaries = lazy(() => import('./pages/Dictionaries'));
const Fleet = lazy(() => import('./pages/Fleet'));
const VehicleTypes = lazy(() => import('./pages/VehicleTypes'));
const Processes = lazy(() => import('./pages/Processes'));
const Planner = lazy(() => import('./pages/Planner'));
const Documents = lazy(() => import('./pages/Documents'));
const Freight = lazy(() => import('./pages/Freight'));
const ProductEdit = lazy(() => import('./pages/ProductEdit'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderNew = lazy(() => import('./pages/OrderNew'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const B2B = lazy(() => import('./pages/B2B'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Channels = lazy(() => import('./pages/Channels'));
const Settings = lazy(() => import('./pages/Settings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const MyDocuments = lazy(() => import('./pages/MyDocuments'));
const Countries = lazy(() => import('./pages/Countries'));
// batch: crm-partner
const Handovers = lazy(() => import('./pages/Handovers'));
const HandoverDetail = lazy(() => import('./pages/HandoverDetail'));
// batch: ws-core — робочі простори; сторінки напрямів створюють батчі C–F (фіксовані імена файлів)
const WorkspaceHome = lazy(() => import('./pages/WorkspaceHome'));
const Overview = lazy(() => import('./pages/Overview'));
const Ritual = lazy(() => import('./pages/Ritual'));
const RitualDetail = lazy(() => import('./pages/RitualDetail'));
const Quality = lazy(() => import('./pages/Quality'));
const QualityDetail = lazy(() => import('./pages/QualityDetail'));
const Marketing = lazy(() => import('./pages/Marketing'));
const Money = lazy(() => import('./pages/Money'));
// batch: p4-A
const Waiting = lazy(() => import('./pages/Waiting'));
const Signals = lazy(() => import('./pages/Signals'));
const Parser = lazy(() => import('./pages/Parser'));

const g = (section: Section, el: ReactNode) => <Gate section={section}>{el}</Gate>;

/** Безпека (2FA) — особиста, доступна всім; журнал змін — власнику й бухгалтеру; решта налаштувань — за роллю. */
function SettingsRoute() {
  const { tab } = useParams();
  if (tab === 'security') return <Settings />;
  if (tab === 'audit') return g('audit', <Settings />);
  if (tab === 'partners') return g('partners', <Settings />); // batch: crm-sponsor
  if (tab === 'workspaces') return g('users', <Settings />); // batch: ws-core (вкладка сама лише для owner)
  if (tab === 'import') return g('users', <Settings />); // batch: p5-E (вкладка сама лише для owner/manager)
  return g('users', <Settings />);
}

/** «/» — головна поточного робочого простору людини. */
function Landing() {
  const me = useAuth((s) => s.me);
  const current = useWorkspace((s) => s.current);
  return <Navigate to={landingFor(me && { ...me, workspace: current ?? me.workspace })} replace />;
}

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <BrowserRouter>
      <Suspense fallback={<Loading className="p-10" />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<AppLayout />}>
            <Route index element={<Landing />} />
            <Route path="today" element={g('today', <Today />)} />
            <Route path="control" element={g('control', <Dashboard />)} />
            <Route path="tasks" element={g('tasks', <Tasks />)} />
            <Route path="leads" element={g('requests', <Leads />)} />
            <Route path="leads/:id" element={g('requests', <LeadDetail />)} />
            <Route path="inbox" element={g('inbox', <Inbox />)} />
            <Route path="inbox/:id" element={g('inbox', <Inbox />)} />
            <Route path="cases" element={g('cases', <Cases />)} />
            <Route path="cases/:id" element={g('cases', <CaseDetail />)} />
            <Route path="products" element={g('catalog', <Catalog />)} />
            <Route path="collections" element={g('collections', <Catalog />)} />
            <Route path="products/new" element={g('catalog', <ProductEdit />)} />
            <Route path="products/:id" element={g('catalog', <ProductCard />)} />
            <Route path="products/:id/edit" element={g('catalog', <ProductEdit />)} />
            <Route path="dictionaries" element={g('dictionaries', <Dictionaries />)} />
            <Route path="fleet" element={g('fleet', <Fleet />)} />
            <Route path="vehicle-types" element={g('vehicleTypes', <VehicleTypes />)} />
            <Route path="processes" element={g('processes', <Processes />)} />
            <Route path="planner" element={g('planner', <Planner />)} />
            <Route path="documents" element={g('documents', <Documents />)} />
            <Route path="freight" element={g('freight', <Freight />)} />
            <Route path="pricing" element={g('pricing', <Pricing />)} />
            <Route path="inventory" element={g('inventory', <Inventory />)} />
            <Route path="channels" element={g('channels', <Channels />)} />
            <Route path="orders" element={g('orders', <Orders />)} />
            <Route path="orders/new" element={g('orders', <OrderNew />)} />
            <Route path="orders/:id" element={g('orders', <OrderDetail />)} />
            <Route path="b2b" element={g('b2b', <B2B />)} />
            <Route path="settings" element={g('users', <Settings />)} />
            <Route path="settings/:tab" element={<SettingsRoute />} />
            <Route path="countries" element={g('countries', <Countries />)} />
            <Route path="notifications" element={g('notifications', <Notifications />)} />
            <Route path="my-documents" element={g('myDocuments', <MyDocuments />)} />
            {/* batch: crm-partner */}
            <Route path="handovers" element={g('handovers', <Handovers />)} />
            <Route path="handovers/:id" element={g('handovers', <HandoverDetail />)} />
            {/* batch: ws-core */}
            <Route path="w/:key" element={g('workspaceHome', <WorkspaceHome />)} />
            <Route path="overview" element={g('overview', <Overview />)} />
            <Route path="ritual" element={g('ritual', <Ritual />)} />
            <Route path="ritual/:id" element={g('ritual', <RitualDetail />)} />
            <Route path="quality" element={g('quality', <Quality />)} />
            <Route path="quality/:id" element={g('quality', <QualityDetail />)} />
            <Route path="marketing" element={g('marketing', <Marketing />)} />
            <Route path="money" element={g('money', <Money />)} />
            <Route path="money/invoices" element={<Navigate to="/money?tab=invoices" replace />} />
            {/* batch: p4-A */}
            <Route path="waiting" element={g('waiting', <Waiting />)} />
            <Route path="signals" element={g('signals', <Signals />)} />
            <Route path="parser" element={g('parser', <Parser />)} />
            <Route path="reports" element={g('reports', <Reports />)} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
      <Toasts />
    </BrowserRouter>
  );
}
